import React from 'react';
import { Box, Typography, Paper, Divider, Link } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

export default function TermsOfService() {
  return (
    <Box sx={{ maxWidth: 900, mx: 'auto' }}>
      <Paper elevation={2} sx={{ p: { xs: 3, md: 5 } }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
          Terms of Service
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
          Last updated: January 31, 2026
        </Typography>

        <Divider sx={{ mb: 4 }} />

        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', mt: 3 }}>
          1. Acceptance of Terms
        </Typography>
        <Typography variant="body1" paragraph>
          By accessing or using the Interclub Competition Manager ("Service"), you agree to be bound by these 
          Terms of Service ("Terms"). If you do not agree to these Terms, you may not use the Service.
        </Typography>

        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', mt: 3 }}>
          2. Description of Service
        </Typography>
        <Typography variant="body1" paragraph>
          The Interclub Competition Manager is a platform for managing interclub figure skating competitions, 
          including skater registration, competition results tracking, season fee payments, and standings 
          calculations. The Service is provided for use by skating clubs, interclub organizations, and their 
          members.
        </Typography>

        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', mt: 3 }}>
          3. User Accounts
        </Typography>
        <Typography variant="body1" paragraph>
          To use certain features of the Service, you must create an account. You agree to:
        </Typography>
        <Box component="ul" sx={{ pl: 3, mb: 2 }}>
          <li><Typography variant="body1">Provide accurate and complete information when creating your account</Typography></li>
          <li><Typography variant="body1">Maintain the security of your account credentials</Typography></li>
          <li><Typography variant="body1">Notify us immediately of any unauthorized access to your account</Typography></li>
          <li><Typography variant="body1">Be responsible for all activities that occur under your account</Typography></li>
        </Box>

        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', mt: 3 }}>
          4. U.S. Figure Skating Membership
        </Typography>
        <Typography variant="body1" paragraph>
          Participation in interclub competitions requires a valid U.S. Figure Skating membership. By 
          registering skaters through the Service, you confirm that:
        </Typography>
        <Box component="ul" sx={{ pl: 3, mb: 2 }}>
          <li><Typography variant="body1">All registered skaters hold current U.S. Figure Skating memberships</Typography></li>
          <li><Typography variant="body1">The membership information provided is accurate</Typography></li>
          <li><Typography variant="body1">You have authority to register the skaters listed in your account</Typography></li>
        </Box>

        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', mt: 3 }}>
          5. Season Registration and Payments
        </Typography>
        <Typography variant="body1" paragraph>
          Season registration fees are processed through Stripe. By submitting a payment:
        </Typography>
        <Box component="ul" sx={{ pl: 3, mb: 2 }}>
          <li><Typography variant="body1">You authorize us to charge the specified amount to your payment method</Typography></li>
          <li>
            <Typography variant="body1">
              You understand that registration fees are generally <strong>non-refundable</strong> unless 
              otherwise specified by your interclub
            </Typography>
          </li>
          <li><Typography variant="body1">If a refund is approved, payment processing fees may be deducted</Typography></li>
          <li><Typography variant="body1">You must complete registration before participating in competitions</Typography></li>
        </Box>

        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', mt: 3 }}>
          6. Competition Rules
        </Typography>
        <Typography variant="body1" paragraph>
          Participation in interclub competitions is governed by the rules established by your interclub 
          organization. These rules include but are not limited to:
        </Typography>
        <Box component="ul" sx={{ pl: 3, mb: 2 }}>
          <li><Typography variant="body1">Eligibility requirements based on skating level</Typography></li>
          <li><Typography variant="body1">Scoring and placement calculations</Typography></li>
          <li><Typography variant="body1">Qualification criteria for regional or national events</Typography></li>
          <li><Typography variant="body1">Code of conduct for skaters, coaches, and parents</Typography></li>
        </Box>
        <Typography variant="body1" paragraph>
          Please refer to the <Link component={RouterLink} to="/rules">Competition Rules</Link> page for 
          detailed information about your interclub's specific rules and regulations.
        </Typography>

        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', mt: 3 }}>
          7. Data Accuracy
        </Typography>
        <Typography variant="body1" paragraph>
          Competition results and standings are calculated based on data uploaded by interclub administrators. 
          While we strive for accuracy, you acknowledge that:
        </Typography>
        <Box component="ul" sx={{ pl: 3, mb: 2 }}>
          <li><Typography variant="body1">Results are dependent on accurate data entry by administrators</Typography></li>
          <li><Typography variant="body1">Errors may occur and should be reported to your interclub administrator</Typography></li>
          <li><Typography variant="body1">Final standings are subject to verification by the interclub organization</Typography></li>
        </Box>

        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', mt: 3 }}>
          8. Acceptable Use
        </Typography>
        <Typography variant="body1" paragraph>
          You agree not to:
        </Typography>
        <Box component="ul" sx={{ pl: 3, mb: 2 }}>
          <li><Typography variant="body1">Use the Service for any unlawful purpose</Typography></li>
          <li><Typography variant="body1">Attempt to gain unauthorized access to the Service or other accounts</Typography></li>
          <li><Typography variant="body1">Submit false or misleading information</Typography></li>
          <li><Typography variant="body1">Interfere with or disrupt the Service or its servers</Typography></li>
          <li><Typography variant="body1">Claim another person's skating records or membership as your own</Typography></li>
        </Box>

        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', mt: 3 }}>
          9. Intellectual Property
        </Typography>
        <Typography variant="body1" paragraph>
          The Service and its original content, features, and functionality are owned by the Service 
          operators and are protected by copyright, trademark, and other intellectual property laws.
        </Typography>

        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', mt: 3 }}>
          10. Disclaimer of Warranties
        </Typography>
        <Typography variant="body1" paragraph>
          THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER 
          EXPRESS OR IMPLIED. WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, 
          OR COMPLETELY SECURE.
        </Typography>

        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', mt: 3 }}>
          11. Limitation of Liability
        </Typography>
        <Typography variant="body1" paragraph>
          TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, 
          SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING WITHOUT LIMITATION, LOSS OF PROFITS, 
          DATA, OR OTHER INTANGIBLE LOSSES.
        </Typography>

        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', mt: 3 }}>
          12. Termination
        </Typography>
        <Typography variant="body1" paragraph>
          We may terminate or suspend your account and access to the Service immediately, without prior 
          notice, for conduct that we believe violates these Terms or is harmful to other users of the 
          Service, us, or third parties, or for any other reason.
        </Typography>

        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', mt: 3 }}>
          13. Changes to Terms
        </Typography>
        <Typography variant="body1" paragraph>
          We reserve the right to modify these Terms at any time. We will notify users of significant 
          changes by posting the new Terms on this page. Your continued use of the Service after changes 
          constitutes acceptance of the new Terms.
        </Typography>

        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', mt: 3 }}>
          14. Privacy
        </Typography>
        <Typography variant="body1" paragraph>
          Your use of the Service is also governed by our <Link component={RouterLink} to="/privacy">Privacy Policy</Link>, 
          which is incorporated into these Terms by reference.
        </Typography>

        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', mt: 3 }}>
          15. Contact
        </Typography>
        <Typography variant="body1" paragraph>
          For questions about these Terms, please contact your interclub administrator.
        </Typography>
      </Paper>
    </Box>
  );
}
